import {
    ILicenseUsageData,
    Interval,
    ITransactionStat,
} from '@models';

import groupBy from 'lodash-es/groupBy';
import * as moment from 'moment';
import { Omit } from 'type-fest';

interface IMomentLicenseUsageData extends Omit<ILicenseUsageData, 'timestamp'> {
    timestamp: moment.Moment;
}
export interface IAggregatedTransactions {
    hasData: boolean;
    chartData: any[];
    chartLabels: string[];
    averageTime: {
        applicationExceptions: string,
        businessExceptions: string,
        succesfulTransactions: string,
    };
}

export const DEFAULT_TRANSACTION_INTERVALS = [
    {
        value: Interval.Hour,
        label: 'Last hour',
        translationKey: 'Intervals_Last_Hour',
    },
    {
        value: Interval.Day,
        label: 'Last day',
        translationKey: 'Intervals_Last_Day',
    },
    {
        value: Interval.Week,
        label: 'Last week',
        translationKey: 'Intervals_Last_Week',
    },
    {
        value: Interval.Month,
        label: 'Last 30 days',
        translationKey: 'Intervals_Last_Month',
    },
];

export const DEFAULT_USAGE_INTERVALS = [
    {
        value: Interval.Week,
        label: 'Last month',
        translationKey: 'Intervals_Last_Week',
    },
    {
        value: Interval.Month,
        label: 'Last month',
        translationKey: 'Intervals_Last_Month',
    },
    {
        value: Interval.ThreeMonths,
        label: 'Last 3 months',
        translationKey: 'Intervals_Last_3_Months',
    },
];

export function unitToFormat(unit: string) {
    switch (unit) {
        case 'minute':
            return 'LT';
        case 'hour':
            return 'lll';
        case 'day':
            return 'll';
    }
    throw new Error(`unitToFormat: unknown unit: [${unit}]`);
}

export function intervalToUnit(interval: Interval) {
    switch (interval) {
        case Interval.Day:
            return 'hour';
        case Interval.Week:
        case Interval.Month:
        case Interval.ThreeMonths:
            return 'day';
    }
    return 'minute';
}

export function aggregateTransactionData(transactionData: ITransactionStat[]) {
    transactionData.sort((a, b) => <any>a.processingTime - <any>b.processingTime);

    const chartData: Array<any> = [],
        chartLabels: Array<string> = [],
        applicationExceptions: Array<any> = [],
        businessExceptions: Array<any> = [],
        succesfulTransactions: Array<any> = [],
        totalArray: Array<any> = [];

    let applicationExceptionTime = 0,
        applicationExceptionCount = 0,
        businessExceptionTime = 0,
        businessExceptionCount = 0,
        succesfulTransactionTime = 0,
        succesfulTransactionCount = 0;

    transactionData.forEach((stat, index) => {
        applicationExceptions[index] = stat.applicationExceptionNo;
        applicationExceptionCount += stat.applicationExceptionNo;
        businessExceptions[index] = stat.businessExceptionNo;
        businessExceptionCount += stat.businessExceptionNo;
        succesfulTransactions[index] = stat.successNo;
        succesfulTransactionCount += stat.successNo;
        totalArray[index] = stat.applicationExceptionNo +
            stat.businessExceptionNo +
            stat.successNo;
        chartLabels[index] = stat.processingTime!.toString();
    });

    chartData[0] = { data: succesfulTransactions, label: 'Successful' };
    chartData[1] = { data: applicationExceptions, label: 'Application Exceptions' };
    chartData[2] = { data: businessExceptions, label: 'Business Exceptions' };
    chartData[3] = { data: totalArray, label: 'Total' };

    transactionData.forEach((transaction: ITransactionStat) => {
        if (transaction.applicationExceptionNo) {
            applicationExceptionTime += transaction.applicationExceptionNo / applicationExceptionCount * transaction.appExProcTime!;
        }
        if (transaction.businessExceptionNo) {
            businessExceptionTime += transaction.businessExceptionNo / businessExceptionCount * transaction.businessExProcTime;
        }
        if (transaction.successNo) {
            succesfulTransactionTime += transaction.successNo / succesfulTransactionCount * transaction.successProcTime;
        }
    });

    return {
        hasData: applicationExceptionCount + businessExceptionCount + succesfulTransactionCount > 0,
        chartData: chartData,
        chartLabels: chartLabels,
        averageTime: {
            applicationExceptions: applicationExceptionTime.toFixed(2),
            businessExceptions: businessExceptionTime.toFixed(2),
            succesfulTransactions: succesfulTransactionTime.toFixed(2),
        },
    } as IAggregatedTransactions;
}

export function aggregateLicenseUsage(data: ILicenseUsageData[], interval: Interval): ILicenseUsageData[] {
    if (!data || !data.length) { return data; }

    // Gather the interval.
    const minDate = moment()
        .startOf('day')
        .subtract(interval, 'days');
    const maxDate = moment()
        .startOf('day')
        .subtract(1, 'day');

    // Group the results by type (by first converting timestamps to strings).
    const groups = groupBy(
        data.map(d =>
            ({
                robotType: d.robotType,
                count: d.count,
                timestamp: moment(d.timestamp),
            }),
        ), 'robotType');

    const zeroFilledGroups: IMomentLicenseUsageData[] = [];

    // Add zero entries for missing dates.
    for (const key of Object.keys(groups)) {
        const subGroup = groupBy(groups[key], (object) => object.timestamp.toJSON());
        for (let date = minDate; date <= maxDate; date = moment(date).add(1, 'day')) {
            const jsonDate = date.toJSON();
            if (!subGroup[jsonDate]) {
                zeroFilledGroups.push({
                    robotType: Number(key),
                    count: 0,
                    timestamp: date,
                });
            } else {
                zeroFilledGroups.push(...subGroup[jsonDate]);
            }
        }
    }

    return zeroFilledGroups.map(
        d => ({
            robotType: d.robotType,
            count: d.count,
            timestamp: d.timestamp.toDate(),
        }),
    );
}
